import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type {
  IDefaultAPIResult,
  IDefaultReducerState,
} from 'types/state.interface';
import type { TSystemAction } from '@timeedit/types/lib/types/systemAction.type';
// TYPES
import api from '../services/api.service';
// ACTIONS
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from '../utils/sliceHelpers';
import type { TAppThunk, TRootState } from './store';

type TSystemActionsState = IDefaultReducerState & {
  allSystemActions: TSystemAction[];
  systemInfo: string;
};

export const initialState: TSystemActionsState = {
  loading: false,
  hasErrors: false,
  allSystemActions: [],
  systemInfo: '',
};

// Slice
const slice = createSlice({
  name: 'systemActions',
  initialState,
  reducers: {
    fetchSystemActionsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchSystemActionsSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TSystemAction>>
    ) => {
      state.allSystemActions = payload.results;
      finishedLoadingSuccess(state);
    },
    fetchSystemActionSuccess: (state, { payload }: PayloadAction<string>) => {
      state.systemInfo = payload;
      finishedLoadingSuccess(state);
    },
    fetchSystemActionsFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const systemActionsLoading = (state: TRootState) =>
  state.systemActions.loading;
export const systemActionsSelector = (state: TRootState) =>
  state.systemActions.allSystemActions;
export const systemInfoSelector = (state: TRootState) =>
  state.systemActions.systemInfo;

// Actions
export const {
  fetchSystemActionsRequest,
  fetchSystemActionsSuccess,
  fetchSystemActionsFailure,
  fetchSystemActionSuccess,
} = slice.actions;

export const fetchSystemActions = (): TAppThunk => async (dispatch) => {
  try {
    dispatch(fetchSystemActionsRequest());
    const systemActions = await api.get({ endpoint: `/system-actions` });
    dispatch(fetchSystemActionsSuccess(systemActions));
  } catch (e) {
    dispatch(fetchSystemActionsFailure());
    return console.error(e);
  }
};

export const fetchSystemActionInfo =
  (actionId: string, valueQuery?: string | number): TAppThunk =>
  async (dispatch) => {
    try {
      dispatch(fetchSystemActionsRequest());
      let url = `/system-actions/${actionId}`;
      if (valueQuery !== undefined) {
        url = `/system-actions/${actionId}?value=${valueQuery}`;
      }
      const { result } = await api.get({
        endpoint: url,
      });
      dispatch(fetchSystemActionSuccess(result));
    } catch (e) {
      dispatch(fetchSystemActionsFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  };
